import { z } from 'zod';

import { AchBatchId, AchBatchScheduleId, CompanyId, SftpSiteId, UserId } from '../BrandedIds';
import { Temporal, zodTemporalPlainTime } from '../Temporal';

/**
 * Local wall-clock time of day at which an automated ACH batch run should fire, constrained to
 * quarter-hour increments (no sub-minute component) since the cron that picks up due schedules runs
 * every 15 minutes.
 */
export const AchBatchScheduleDeliveryTime = zodTemporalPlainTime.refine(
  (t: Temporal.PlainTime) =>
    t.minute % 15 === 0 && t.second === 0 && t.millisecond === 0 && t.microsecond === 0 && t.nanosecond === 0,
  { message: 'Delivery times must be on a 15-minute boundary' },
);

/**
 * Configuration for automatically generating an ACH batch file at one or more times each (business)
 * day and delivering it to an SFTP site. Distinct from recurring company reports.
 */
export const AchBatchSchedule = z.object({
  id: AchBatchScheduleId,
  companyId: CompanyId,
  // The user who configured the schedule; scheduled batch runs are attributed to them since batch
  // creation requires a real user and there is no system-user concept.
  createdByUserId: UserId,
  sftpSiteId: SftpSiteId,
  // Local wall-clock times of day (in `timeZone`) at which to run. At least one.
  deliveryTimes: z.array(AchBatchScheduleDeliveryTime).min(1),
  // IANA timezone the delivery times are interpreted in (e.g. "America/New_York").
  timeZone: z.string(),
  enabled: z.boolean(),
  // Customer-configured calendar dates (UTC midnight) on which runs are skipped, in addition to
  // the weekends/holidays that are always skipped.
  blackoutDates: z.array(z.date()),
  // The next instant the schedule should fire; undefined when disabled or nothing is scheduled.
  nextRunAt: z.date().optional(),
  // Observability for the most recent run.
  lastRunAt: z.date().optional(),
  lastBatchId: AchBatchId.optional(),
  lastError: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type AchBatchSchedule = z.infer<typeof AchBatchSchedule>;
